import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Plus, Trash2, Edit2, X, Check, ChevronRight, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gradeOptions = ['K', '1', '2', '3-4', '5-6', '7-8', '9-12'];
const modalityOptions = ['listening', 'speaking', 'reading', 'writing'];
const typeOptions = [
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'constructed_response', label: 'Constructed Response' },
  { value: 'speaking_prompt', label: 'Speaking Prompt' },
];

const emptyQuestion = {
  grade_level: 'K',
  modality: 'reading',
  question_type: 'multiple_choice',
  question_text: '',
  correct_answer: '',
  answer_options: ['', '', '', ''],
  stimulus: '',
  max_points: 1,
};

const CustomQuestionCreator = () => {
  const [questions, setQuestions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyQuestion });
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchQuestions = async () => {
    try {
      const res = await axios.get(`${API}/questions?custom_only=true&include_standards=true`);
      setQuestions(res.data.questions || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQuestions(); }, []);

  const handleSave = async () => {
    if (!form.question_text.trim()) { toast.error('Question text is required'); return; }
    if (!form.correct_answer.trim()) { toast.error('Correct answer is required'); return; }

    const payload = {
      ...form,
      answer_options: form.question_type === 'multiple_choice'
        ? form.answer_options.filter(o => o.trim())
        : [],
    };

    try {
      if (editing) {
        await axios.put(`${API}/custom-questions/${editing}`, payload);
        toast.success('Question updated');
      } else {
        await axios.post(`${API}/custom-questions`, payload);
        toast.success('Question created');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ ...emptyQuestion });
      fetchQuestions();
    } catch (e) {
      toast.error('Failed to save question');
    }
  };

  const handleEdit = (q) => {
    setForm({
      grade_level: q.grade_level,
      modality: q.modality,
      question_type: q.question_type,
      question_text: q.question_text,
      correct_answer: q.correct_answer,
      answer_options: q.answer_options?.length >= 4 ? q.answer_options : [...(q.answer_options || []), ...Array(4 - (q.answer_options?.length || 0)).fill('')],
      stimulus: q.stimulus || '',
      max_points: q.max_points || 1,
    });
    setEditing(q.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await axios.delete(`${API}/custom-questions/${id}`);
      toast.success('Question deleted');
      fetchQuestions();
    } catch (e) {
      toast.error('Failed to delete question');
    }
  };

  const updateOption = (idx, val) => {
    const opts = [...form.answer_options];
    opts[idx] = val;
    setForm({ ...form, answer_options: opts });
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Loading...</div>;

  return (
    <div data-testid="custom-question-creator">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-lg">Custom Questions</h3>
          <p className="text-sm text-gray-500">{questions.length} custom question{questions.length !== 1 && 's'} created</p>
        </div>
        <Button
          className="btn-sharp-primary h-10"
          onClick={() => { setShowForm(true); setEditing(null); setForm({ ...emptyQuestion }); }}
          data-testid="new-question-btn"
        >
          <Plus size={16} className="mr-2" /> New Question
        </Button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="border-2 border-[#002FA7] p-5 mb-6 bg-blue-50" data-testid="question-form">
          <h4 className="font-bold mb-4">{editing ? 'Edit Question' : 'Create New Question'}</h4>
          
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Grade Level</label>
              <select value={form.grade_level} onChange={e => setForm({ ...form, grade_level: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="q-grade-select">
                {gradeOptions.map(g => <option key={g} value={g}>Grade {g}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Modality</label>
              <select value={form.modality} onChange={e => setForm({ ...form, modality: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="q-modality-select">
                {modalityOptions.map(m => <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Question Type</label>
              <select value={form.question_type} onChange={e => setForm({ ...form, question_type: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="q-type-select">
                {typeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="text-xs font-semibold text-gray-500 block mb-1">Stimulus / Context (optional)</label>
            <textarea value={form.stimulus} onChange={e => setForm({ ...form, stimulus: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm min-h-[60px]" placeholder="Add a reading passage, listening context, or image description..." data-testid="q-stimulus-input" />
          </div>

          <div className="mb-4">
            <label className="text-xs font-semibold text-gray-500 block mb-1">Question Text *</label>
            <textarea value={form.question_text} onChange={e => setForm({ ...form, question_text: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm min-h-[80px]" placeholder="Enter your question..." data-testid="q-text-input" />
          </div>

          {form.question_type === 'multiple_choice' && (
            <div className="mb-4">
              <label className="text-xs font-semibold text-gray-500 block mb-2">Answer Options</label>
              <div className="grid md:grid-cols-2 gap-2">
                {form.answer_options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className={`w-6 h-6 flex items-center justify-center text-xs font-bold ${opt === form.correct_answer ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {String.fromCharCode(65 + i)}
                    </span>
                    <input
                      value={opt}
                      onChange={e => updateOption(i, e.target.value)}
                      className="flex-1 border border-gray-300 px-3 py-2 text-sm"
                      placeholder={`Option ${String.fromCharCode(65 + i)}`}
                      data-testid={`q-option-${i}`}
                    />
                    <button
                      onClick={() => setForm({ ...form, correct_answer: opt })}
                      className={`text-xs px-2 py-1 ${opt === form.correct_answer ? 'bg-green-100 text-green-700 font-bold' : 'bg-gray-100 text-gray-500'}`}
                    >
                      Correct
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {form.question_type !== 'multiple_choice' && (
            <div className="mb-4">
              <label className="text-xs font-semibold text-gray-500 block mb-1">Expected Answer / Rubric</label>
              <textarea value={form.correct_answer} onChange={e => setForm({ ...form, correct_answer: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm min-h-[60px]" placeholder="What is the expected answer or key rubric points?" data-testid="q-answer-input" />
            </div>
          )}

          <div className="flex gap-2 mt-4">
            <Button onClick={handleSave} className="btn-sharp-primary h-10" data-testid="save-question-btn">
              <Check size={14} className="mr-2" /> {editing ? 'Update' : 'Create'}
            </Button>
            <Button variant="outline" className="btn-sharp h-10" onClick={() => { setShowForm(false); setEditing(null); }}>
              <X size={14} className="mr-2" /> Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Question List */}
      {questions.length === 0 && !showForm ? (
        <div className="border-2 border-gray-200 p-12 text-center">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="font-bold text-lg mb-2">No Custom Questions</h3>
          <p className="text-gray-500 text-sm">Create your own questions to supplement the MOSAIC question bank.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {questions.map(q => (
            <div key={q.id} className="border-2 border-gray-200 p-4 flex items-start justify-between hover:border-gray-400 transition-colors" data-testid={`custom-q-${q.id}`}>
              <div className="flex-1 mr-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 font-medium">Grade {q.grade_level}</span>
                  <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 font-medium capitalize">{q.modality}</span>
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600">{q.question_type?.replace('_', ' ')}</span>
                  {q.wida_standard && (
                    <span className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid={`wida-std-${q.id}`} title={q.wida_mpi || ''}>
                      ELD {q.wida_eld_level || '—'}
                    </span>
                  )}
                </div>
                <p className="text-sm font-medium">{q.question_text}</p>
                {q.answer_options?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {q.answer_options.map((opt, i) => (
                      <span key={i} className={`text-xs px-2 py-1 ${opt === q.correct_answer ? 'bg-green-100 text-green-700 font-bold' : 'bg-gray-100 text-gray-500'}`}>
                        {String.fromCharCode(65 + i)}) {opt}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-8" onClick={() => handleEdit(q)} data-testid={`edit-q-${q.id}`}>
                  <Edit2 size={14} />
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-red-600 hover:bg-red-50" onClick={() => handleDelete(q.id)} data-testid={`delete-q-${q.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomQuestionCreator;
